/* global globalThis, navigator, document */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { FALLBACK, Locale, resolveLocale, SUPPORTED } from "./locales";
import en from "../locales/en.json";

type Catalogue = Record<string, unknown>;

// English is bundled: it is the fallback for any key a catalogue lacks. The
// other 19 load on demand, one chunk each, so a user downloads only their own
// language rather than ~290 kB of catalogues they will never see.
const lazyCatalogues = import.meta.glob<{ default: Catalogue }>([
  "../locales/*.json",
  "!../locales/en.json",
]);

async function loadCatalogue(locale: Locale): Promise<Catalogue | undefined> {
  if (locale === FALLBACK) return undefined;
  const load = lazyCatalogues[`../locales/${locale}.json`];
  if (!load) return undefined;
  try {
    return (await load()).default;
  } catch {
    // A failed chunk fetch should cost the user their language, not the pane.
    return undefined;
  }
}

/**
 * The language Outlook is running in, or the browser's where there is no Office
 * host (the sign-in dialog and the public guide both run without one).
 */
export function detectLocale(): Locale {
  try {
    const office = (globalThis as { Office?: { context?: { displayLanguage?: string } } }).Office;
    const fromOffice = office?.context?.displayLanguage;
    if (fromOffice) {
      return resolveLocale(fromOffice);
    }
  } catch {
    // No Office host; fall through to the browser.
  }
  return resolveLocale(typeof navigator !== "undefined" ? navigator.language : undefined);
}

/**
 * Initialise translations. Safe to call more than once. Resolves once the
 * catalogue is in place, so the first render is already in the right language.
 */
export async function initI18n(locale: Locale = detectLocale()): Promise<typeof i18n> {
  const catalogue = await loadCatalogue(locale);
  if (catalogue && i18n.isInitialized && !i18n.hasResourceBundle(locale, "translation")) {
    i18n.addResourceBundle(locale, "translation", catalogue);
  }
  if (!i18n.isInitialized) {
    // The "translation" level is required: without it i18next treats each
    // catalogue's first key as a namespace and every lookup returns the key.
    const resources: Record<string, { translation: Catalogue }> = { en: { translation: en } };
    if (catalogue) resources[locale] = { translation: catalogue };
    await i18n.use(initReactI18next).init({
      resources,
      lng: locale,
      fallbackLng: FALLBACK,
      supportedLngs: SUPPORTED as unknown as string[],
      interpolation: { escapeValue: false }, // React escapes already
      returnNull: false,
    });
  } else if (i18n.language !== locale) {
    await i18n.changeLanguage(locale);
  }
  // <html lang> drives screen-reader pronunciation and CSS hyphenation; left at
  // the page's "en" a Greek pane is read aloud with English phonetics.
  if (typeof document !== "undefined") document.documentElement.lang = i18n.language;
  return i18n;
}

export { i18n };
export * from "./locales";
